<template>
  <div class="sun-anchor-editor">
    <span class="sun-anchor-editor__label">{{ label }}</span>
    <div class="sun-anchor-editor__control">
      <select class="sun-anchor-editor__type" :value="modelValue.type" @change="setType($event)">
        <option v-for="anchor in SUN_ANCHOR_TYPES" :key="anchor.value" :value="anchor.value">
          {{ sunLabel(anchor.label) }}
        </option>
      </select>

      <input
        v-if="modelValue.type === 'time'"
        class="sun-anchor-editor__time"
        type="time"
        :value="modelValue.time || '08:00'"
        @change="setTime($event)" />

      <div v-else class="sun-anchor-editor__offset">
        <span>±</span>
        <input
          class="sun-anchor-editor__offset-input"
          type="number"
          min="-180"
          max="180"
          :value="modelValue.offset_minutes ?? 0"
          @change="setOffset($event)" />
        <span>min</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { SUN_ANCHOR_TYPES, SunWindowAnchor, defaultAnchor, sunLabel } from '../../../utils/sunTracker';

const props = defineProps<{
  label: string;
  modelValue: SunWindowAnchor;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: SunWindowAnchor): void;
}>();

const anchor = computed(() => props.modelValue);

function commit(next: SunWindowAnchor) {
  emit('update:modelValue', next);
}

function setType(event: Event) {
  const type = (event.target as HTMLSelectElement).value as SunWindowAnchor['type'];
  commit({ ...defaultAnchor(type), type });
}

function setTime(event: Event) {
  const time = (event.target as HTMLInputElement).value;
  commit({ ...anchor.value, type: 'time', time });
}

function setOffset(event: Event) {
  const offset = Number((event.target as HTMLInputElement).value) || 0;
  commit({ ...anchor.value, offset_minutes: offset });
}
</script>

<style scoped>
.sun-anchor-editor {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sun-anchor-editor__label {
  font-size: 11px;
  font-weight: 600;
  color: rgb(100, 116, 139);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.sun-anchor-editor__control {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sun-anchor-editor__type,
.sun-anchor-editor__time,
.sun-anchor-editor__offset-input {
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(255, 255, 255, 0.04);
  color: inherit;
  font-size: 12px;
  padding: 6px 8px;
}

.sun-anchor-editor__type {
  min-width: 96px;
}

.sun-anchor-editor__offset {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgb(148, 163, 184);
}

.sun-anchor-editor__offset-input {
  width: 72px;
}
</style>
